import React from 'react';
import styled from 'styled-components';

import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';

import ArrowForward from '@mui/icons-material/ArrowForward';

import { useAnalytics, useCurrentDebrief } from 'ff/debrief/hooks';

import { Tracklog } from 'ff/debrief/models';
import { LinkToShared, LinkToHome } from 'ff/debrief/router';
import { getAccountUuid, isFeatureEnabled } from 'ff/common/ffAppUtils';
import SegmentScore, {
    NoScore,
} from 'ff/debrief/components/segments/SegmentScore';
import TracklogLogbookIcon from 'ff/debrief/components/sidebar/TracklogLogbookIcon';
import { hasDebriefLicense } from 'ff/debrief/utils';
import OwnedByBadge from 'ff/debrief/components/sidebar/OwnedByBadge';
import SharedFlightBadge from 'ff/debrief/components/sidebar/SharedFlightBadge';

type Props = Pick<
    Tracklog,
    | 'accountUuid'
    | 'username'
    | 'derivedOrigin'
    | 'derivedDestination'
    | 'timestampStart'
    | 'tailNumber'
    | 'trackUuid'
    | 'analysisSummary'
    | 'logbookAssociationDetails'
    | 'shareDetailsSummary'
> & {
    selected?: boolean;
    onClick?: (tracklogId: string) => void;
    shared?: boolean;
    sharedList?: boolean;
};

const TracklogListItem: React.FC<Props> = props => {
    const { isScoreModeEnabled } = useCurrentDebrief();
    const { logTrackLogSelected } = useAnalytics();
    const date =
        props.timestampStart &&
        new Date(props.timestampStart).toLocaleString(
            undefined,
            dateFormatOptions
        );

    const { status, score } = props.analysisSummary || {};

    const icon = React.useMemo(() => {
        if (
            isFeatureEnabled('debrief_features') &&
            (props.sharedList || hasDebriefLicense())
        ) {
            if (!isScoreModeEnabled) return null;
            if (status === 'PENDING' || status === 'PROCESSING') {
                return <Skeleton variant="rounded" height={20} width={27} />;
            } else if (status === 'COMPLETED' && score !== null) {
                return <SegmentScore score={score} />;
            } else {
                return <NoScore />;
            }
        }
    }, [status, score, props.selected, isScoreModeEnabled]);

    const hasAssociation = props.logbookAssociationDetails.length > 0;
    const logbookCount = props.logbookAssociationDetails
        ? props.logbookAssociationDetails.length
        : 0;
    const logbookId = hasAssociation
        ? props.logbookAssociationDetails[0].logbookEntryID
        : null;

    const logbookIcon =
        isFeatureEnabled('logbook_association') && logbookCount > 0 ? (
            <>
                {' | '}
                <TracklogLogbookIcon
                    logbookAssociationDetails={[]}
                    selected={props.selected}
                    logbookId={logbookId}
                />
                {logbookCount}
            </>
        ) : null;

    const SecondaryActions = () => (
        <div style={{ display: 'flex', alignItems: 'center' }}>{icon}</div>
    );

    // The backend excludes the username from the tracklog list in cases that the
    // debriefing user shouldn't see it due to privacy concerns.  So, we only display
    // the owned-by badge if the username is present.
    const showOwnedByBadge =
        getAccountUuid() !== props.accountUuid && !!props.username;

    const showSharedFlightBadge =
        props.shareDetailsSummary && !props.sharedList;

    const handleClick = () => {
        logTrackLogSelected({
            score: props.analysisSummary?.score || null,
            shared: showSharedFlightBadge,
        });
    };

    return (
        <ListItem
            component={props.shared ? LinkToShared : LinkToHome}
            tracklogId={props.trackUuid}
            shareId={props.shareDetailsSummary?.shareId}
            secondaryAction={<SecondaryActions />}
            disablePadding
        >
            <ListItemContainer selected={props.selected} onClick={handleClick}>
                <Terminals
                    to={props.derivedDestination}
                    from={props.derivedOrigin}
                />
                <DateField>{date}</DateField>
                <AircraftField>
                    {props.tailNumber}
                    {logbookIcon}
                </AircraftField>
                {showOwnedByBadge && (
                    <OwnedByBadge ownerUsername={props.username} />
                )}
                {showSharedFlightBadge && <SharedFlightBadge />}
            </ListItemContainer>
        </ListItem>
    );
};

export const Terminals: React.FC<{ to: string; from: string }> = ({
    to,
    from,
}) => (
    <TerminalsContainer>
        <TerminalField>{from}</TerminalField>
        <ArrowForward sx={{ marginX: '6px' }} fontSize="small" />
        <TerminalField>{to}</TerminalField>
    </TerminalsContainer>
);

const dateFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
};

const ListItemContainer = styled(ListItemButton)`
    flex-direction: column;
    align-items: flex-start;
    border-radius: 4px;
    padding: 8px 12px;

    &.Mui-selected {
        background-color: rgb(52, 152, 219);
        color: #ffffff !important;

        &:hover {
            background-color: rgba(52, 152, 219);
        }

        & p {
            color: #ffffff !important;
        }
    }
`;

const TerminalsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TerminalField = styled(Typography)`
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-style: normal;
    line-height: 21px;
`;

const DateField = styled(Typography)`
    color: #424853;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
`;

const AircraftField = styled(Typography)`
    color: #424853;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
`;

export default TracklogListItem;
